import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

interface Plan {
  id: string;
  name: string;
  type: string;
  price: number;
  iban_validations: number;
  bank_info_requests: number;
  extra_credits_price: number | null;
}

interface UserPlanData {
  plans?: { name: string } | null;
}

const PricingPage = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [userPlanName, setUserPlanName] = useState<string | null>(null);
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const fetchPlans = async () => {
      const { data, error } = await supabase.from("plans").select("*");
      if (!error && data) {
        setPlans(data);
      }
    };
    fetchPlans();

    const fetchUserPlan = async () => {
      if (isAuthenticated && user) {
        const { data, error } = await supabase
          .from("user_subscriptions")
          .select("plans(name)")
          .eq("user_id", user.id)
          .single();
    
        const typedData = data as unknown as UserPlanData;
    
        if (!error && typedData?.plans?.name) {
          setUserPlanName(typedData.plans.name);
        }
      }
    };

    fetchUserPlan();
  }, [isAuthenticated, user]);

  const freePlan = plans.find(p => p.name === "Free Tier");

const subscriptionPlans = plans
  .filter(p => p.type === "subscription" && p.name !== "Free Tier")
  .sort((a, b) => a.price - b.price);

const payAsYouGoPlans = plans
  .filter(p => p.type === "pay-as-you-go")
  .sort((a, b) => a.price - b.price);


  return (
    <div className="flex flex-col min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-12 flex-grow">
        <h1 className="text-4xl font-bold text-center text-teal-700 mb-10">Pricing</h1>

        {/* Free Plan */}
        {freePlan && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-2 text-gray-700">🎁 Free Plan</h2>
            <p className="text-gray-600 mb-4">Get started for free — this plan is perfect for testing and early usage. Credits renew every month automatically.</p>
            <PlanCard plan={freePlan} userPlanName={userPlanName}/>
          </div>
        )}

        {/* Subscription Plans */}
        {subscriptionPlans.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-2 text-gray-700">📦 Subscription Plans</h2>
            <p className="text-gray-600 mb-4">Choose a monthly plan that fits your needs. Unused credits reset every month.</p>
            <div className="grid md:grid-cols-2 gap-6">
              {subscriptionPlans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} userPlanName={userPlanName} />
              ))}
            </div>
          </div>
        )}

        {/* Pay-As-You-Go Plans */}
        {payAsYouGoPlans.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold mb-2 text-gray-700">🧾 Pay-As-You-Go Plans</h2>
            <p className="text-gray-600 mb-4">Purchase credits once and use them anytime — they never expire.</p>
            <div className="grid md:grid-cols-2 gap-6">
              {payAsYouGoPlans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} userPlanName={userPlanName}/>
              ))}
            </div>
          </div>
        )}

        <div className="text-center text-sm text-gray-500 mt-16">
          Need higher limits or want to integrate our API into your service?{" "}
          <Link to="/contact" className="text-teal-600 hover:underline">
            Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
};

const PlanCard = ({ plan, userPlanName }: { plan: Plan; userPlanName: string | null }) => {
  const isFree = plan.name === "Free Tier";
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const isCurrentPlan = plan.name === userPlanName;

  // Map plan name to short query param value
  const getPlanQueryValue = (name: string): string => {
    switch (name.toLowerCase()) {
      case "basic subscription":
        return "basic";
      case "pro subscription":
        return "pro";
      case "pay-as-you-go small":
        return "paygo-small";
      case "pay-as-you-go large":
        return "paygo-large";
      case "enterprise":
        return "enterprise";
      default:
        return "";
    }
  };

  const handleChoose = () => {
    const planQueryValue = getPlanQueryValue(plan.name);

    if (isAuthenticated) {
      navigate(`/request-invoice?plan=${planQueryValue}`);
    } else {
      navigate(`/login?redirectTo=${encodeURIComponent(`/request-invoice?plan=${planQueryValue}`)}`);
    }
  };

  return (
    <div className="border rounded-lg p-6 bg-white shadow">
      <h3 className="text-xl font-bold text-teal-700 mb-2">{plan.name}</h3>
      <p className="text-gray-700 mb-2">{plan.type === 'subscription' ? 'Subscription' : 'Pay-as-you-go'}</p>
      <p className="text-2xl font-semibold mb-4">${plan.price.toFixed(2)}</p>
      <ul className="mb-4 text-sm text-gray-600 space-y-1">
        <li>✅ {plan.iban_validations} IBAN validations</li>
        <li>✅ {plan.bank_info_requests} Bank info requests</li>
        {plan.extra_credits_price !== null && (
          <li>➕ Extra credits at ${plan.extra_credits_price.toFixed(2)}</li>
        )}
      </ul>
      <button
        onClick={handleChoose}
        disabled={isCurrentPlan}
        className={`mt-2 w-full py-2 rounded 
          ${isCurrentPlan 
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
            : isFree 
              ? 'bg-teal-600 text-white hover:bg-teal-700' 
              : 'bg-gray-800 text-white hover:bg-gray-900'
          }`}
      >
        {isCurrentPlan ? 'Current Plan' : isFree ? 'Get Started' : 'Choose Plan'}
      </button>
    </div>
  );
};


export default PricingPage;